import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Observable, Subscription, interval, timer } from 'rxjs';
import { retry, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface JobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface JobStatus {
  job_id: string;
  /**
   * Job lifecycle state. The chunked cloud flow adds states the legacy
   * in-process flow never produced ('uploaded', 'chunking', 'queued') plus
   * 'completed_with_errors', which is terminal AND downloadable - results
   * exist for the chunks that succeeded.
   */
  status:
    | 'pending'
    | 'uploaded'
    | 'chunking'
    | 'queued'
    | 'processing'
    | 'completed'
    | 'completed_with_errors'
    | 'failed'
    | 'cancelled';
  progress: number;
  message: string;
  started_at: string;
  completed_at?: string;
  error?: string;
  /**
   * Server-side status of the results ZIP packaging. The archive is now
   * pre-built in the background as soon as processing completes so large
   * runs (500+ LAS files) do not time out on download.
   */
  zip_status?: 'building' | 'ready' | 'failed';
  zip_size?: number;
}

export interface LogsResponse {
  logs: string[];
}

export interface ConfigUploadResponse {
  path: string;
}

export interface PreflightSummary {
  total_files: number;
  valid_files: number;
  failed_files: number;
  wells_detected: number;
  wells_with_multiple_files: number;
  wells_with_overlaps: number;
  files_missing_well_id: number;
  files_missing_depth_headers: number;
  total_curves: number;
  unique_mnemonics: number;
  unit_conflict_mnemonics: number;
}

export interface PreflightWell {
  well_id: string;
  file_count: number;
  overlap_count: number;
  gap_count: number;
  unit_conflicts: string[];
}

export interface PreflightFile {
  path: string;
  status: 'valid' | 'failed';
  well_id: string;
  issues: string[];
  start?: number;
  stop?: number;
  step?: number;
  curve_count: number;
}

export interface PreflightReport {
  generated_at: string;
  source_folder: string;
  summary: PreflightSummary;
  warnings: string[];
  files: PreflightFile[];
  wells: PreflightWell[];
}

export interface PreflightResponse {
  job_id?: string;
  report: PreflightReport;
}

/** One write-only SAS URL, scoped to a single blob path. */
export interface UploadUrlItem {
  file_name: string;
  blob_path: string;
  upload_url: string;
}

export interface UploadUrlsResponse {
  job_id: string;
  container: string;
  expires_in_minutes: number;
  files: UploadUrlItem[];
}

export interface ConfirmUploadResponse {
  job_id: string;
  status: string;
  uploaded_count: number;
  expected_count: number;
  /** Files the server could not find in Blob Storage; retry just these. */
  missing_files: string[];
}

/** Aggregate progress across a direct-to-Blob upload batch. */
export interface UploadProgress {
  bytesUploaded: number;
  totalBytes: number;
  filesCompleted: number;
  totalFiles: number;
  percent: number;
}

export interface ChunkSummary {
  chunk_id: string;
  file_count: number;
  uwi_count: number;
  size_mb: number;
}

export interface CreateChunksResponse {
  job_id: string;
  total_files: number;
  total_chunks: number;
  summary: any;
  chunks: ChunkSummary[];
}

/** Short-lived SAS pointing at the results ZIP in Blob Storage. */
export interface DownloadUrlResponse {
  job_id: string;
  download_url: string;
  filename: string;
  size_bytes?: number;
  expires_in_minutes: number;
}

@Injectable({
  providedIn: 'root'
})
export class LogmergeApiService {
  private apiUrl = environment.apiUrl;

  /**
   * Statuses that mean the job is still in flight.
   *
   * Dispatch now returns 'queued', so omitting it here would end the poll
   * subscription on the very first tick and freeze the UI mid-progress while
   * the job actually runs to completion in the background.
   */
  private static readonly ACTIVE_STATUSES: string[] = [
    'pending', 'uploaded', 'chunking', 'queued', 'processing'
  ];

  constructor(private http: HttpClient) {}

  /** Open native OS folder picker, returns selected path */
  browseFolder(title: string = 'Select Folder'): Observable<{path: string}> {
    return this.http.get<{path: string}>(`${this.apiUrl}/api/browse-folder`, { params: { title } });
  }

  /**
   * Upload LAS files through the API as multipart form data.
   *
   * @deprecated Use the direct-to-Blob flow instead
   * (`createJob` -> `getUploadUrls` -> `uploadFileToBlob` -> `confirmUpload`).
   * This route streams every byte through the API process, which does not
   * scale past a few dozen files. Kept for local development where Blob
   * Storage is not configured.
   */
  uploadFiles(files: File[]): Observable<JobResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file, file.name);
    });
    return this.http.post<JobResponse>(`${this.apiUrl}/api/upload`, formData);
  }

  // --- Direct-to-Blob upload (SAS) -----------------------------------------
  // File bytes go straight from the browser to Azure Blob Storage, so the API
  // never sits on the upload data path. Three steps: create the job, get one
  // write-only SAS URL per file, PUT the bytes, then confirm what landed.

  /**
   * Create an empty job so upload URLs can be scoped to a job_id.
   *
   * @param notifyEmail Optional address the backend emails when the job
   *   reaches a terminal state. The mail carries a `?job=<id>` deep link
   *   back into this app, never a storage URL.
   */
  createJob(jobName?: string, notifyEmail?: string | null): Observable<JobResponse> {
    return this.http.post<JobResponse>(`${this.apiUrl}/api/jobs`, {
      job_name: jobName ?? null,
      notify_email: notifyEmail ?? null
    });
  }

  /** Request one short-lived write-only SAS URL per file. */
  getUploadUrls(jobId: string, files: File[]): Observable<UploadUrlsResponse> {
    return this.http.post<UploadUrlsResponse>(`${this.apiUrl}/api/jobs/${jobId}/upload-urls`, {
      files: files.map(f => ({ name: f.name, size: f.size }))
    });
  }

  /**
   * PUT one file directly to Blob Storage using its SAS URL.
   *
   * Emits `HttpEvent`s so callers can track byte-level progress.
   *
   * Note: this request must NOT carry application auth headers. Azure rejects
   * requests that mix a SAS token with an unexpected Authorization header, so
   * if an `HttpInterceptor` is ever added to this app it must skip absolute
   * `*.blob.core.windows.net` URLs.
   */
  uploadFileToBlob(uploadUrl: string, file: File): Observable<HttpEvent<unknown>> {
    return this.http.put(uploadUrl, file, {
      headers: new HttpHeaders({
        // Required by Azure for a single-shot block blob PUT.
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'application/octet-stream'
      }),
      reportProgress: true,
      observe: 'events'
    }).pipe(
      // Blob Storage occasionally answers a burst of PUTs with a transient
      // 5xx/network blip (nothing to do with the SAS itself), and at tens
      // of thousands of requests that is near-certain to happen at least
      // once. A short exponential backoff clears it without bothering the
      // user; genuinely bad requests (expired SAS, 4xx) still exhaust the
      // retries and surface as a failure for this one file.
      retry({ count: 3, delay: (_err, retryCount) => timer(Math.min(1000 * 2 ** (retryCount - 1), 8000)) })
    );
  }

  /**
   * Upload every file to Blob Storage with bounded concurrency, emitting
   * aggregate progress as bytes land.
   *
   * Concurrency is capped because browsers queue requests per host anyway, and
   * an unbounded fan-out of tens of thousands of PUTs just adds memory
   * pressure without going any faster. Blob Storage itself has no trouble
   * with far more parallelism than a single browser tab can generate.
   *
   * A single file that still fails after retries does NOT abort the rest of
   * the batch - at 50k files, aborting everything for one bad file would
   * mean restarting the whole upload from scratch. It is instead left for
   * ``confirmUpload``'s existing missing-file diff to catch, so the caller
   * can retry just the handful that failed.
   */
  uploadFilesToBlob(
    files: File[],
    urls: UploadUrlItem[],
    concurrency: number = 12
  ): Observable<UploadProgress> {
    const byName = new Map(urls.map(u => [u.file_name, u]));
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

    return new Observable<UploadProgress>(subscriber => {
      // Per-file loaded byte counts, so re-emitting aggregate progress is just
      // a sum and a file that reports progress twice can't double-count.
      const loaded = new Map<string, number>();
      let filesCompleted = 0;
      let index = 0;
      let active = 0;
      let cancelled = false;
      const subs: Subscription[] = [];

      const emit = () => {
        let bytesUploaded = 0;
        loaded.forEach(v => (bytesUploaded += v));
        subscriber.next({
          bytesUploaded,
          totalBytes,
          filesCompleted,
          totalFiles: files.length,
          percent: totalBytes > 0 ? Math.round((bytesUploaded / totalBytes) * 100) : 0
        });
      };

      const pump = () => {
        if (cancelled) {
          return;
        }
        if (filesCompleted === files.length) {
          subscriber.complete();
          return;
        }
        while (active < concurrency && index < files.length) {
          const file = files[index++];
          const target = byName.get(file.name);
          if (!target) {
            // No SAS URL for this file is a request-shape bug, not a transient
            // upload failure - still worth surfacing, but count it as a
            // finished attempt so the batch does not hang forever.
            filesCompleted++;
            emit();
            continue;
          }
          active++;
          const sub = this.uploadFileToBlob(target.upload_url, file).subscribe({
            next: event => {
              if (event.type === HttpEventType.UploadProgress) {
                loaded.set(file.name, event.loaded);
                emit();
              }
            },
            error: () => {
              // Leave it unconfirmed rather than aborting every other in-flight
              // and queued upload - confirmUpload() reports it in missing_files.
              loaded.set(file.name, file.size);
              filesCompleted++;
              active--;
              emit();
              pump();
            },
            complete: () => {
              // Snap to the full size: the last progress event can lag the
              // actual completion, which would leave the bar short of 100%.
              loaded.set(file.name, file.size);
              filesCompleted++;
              active--;
              emit();
              pump();
            }
          });
          subs.push(sub);
        }
      };

      emit();
      pump();

      return () => {
        cancelled = true;
        subs.forEach(s => s.unsubscribe());
      };
    });
  }

  /** Ask the server to verify which files actually landed in Blob Storage. */
  confirmUpload(jobId: string, files: File[]): Observable<ConfirmUploadResponse> {
    return this.http.post<ConfirmUploadResponse>(
      `${this.apiUrl}/api/jobs/${jobId}/confirm-upload`,
      { files: files.map(f => f.name) }
    );
  }

  /** Upload log suite .ini config file */
  uploadLogSuiteConfig(file: File): Observable<ConfigUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ConfigUploadResponse>(`${this.apiUrl}/api/upload-config`, formData);
  }

  /**
   * Group a job's uploaded blobs into chunks.
   *
   * Must complete before `dispatchJob`: the backend rejects a dispatch for a
   * job with no chunk rows, so these two cannot be fired in parallel.
   */
  createChunks(jobId: string, maxFilesPerChunk?: number): Observable<CreateChunksResponse> {
    let params = new HttpParams();
    if (maxFilesPerChunk) {
      params = params.set('max_files_per_chunk', String(maxFilesPerChunk));
    }
    return this.http.post<CreateChunksResponse>(
      `${this.apiUrl}/api/jobs/${jobId}/create-chunks`, {}, { params }
    );
  }

  /**
   * Hand the job's chunks to the worker queue.
   *
   * Returns as soon as the messages are enqueued (status 'queued'). The work
   * itself runs in worker containers, so all subsequent progress must come
   * from polling - this response does not mean processing has finished.
   */
  dispatchJob(jobId: string, config: any): Observable<JobResponse> {
    return this.http.post<JobResponse>(`${this.apiUrl}/api/jobs/${jobId}/process`, config);
  }

  /** Start processing directly from a local folder path (no upload) */
  startProcessingFromPath(config: any): Observable<JobResponse> {
    return this.http.post<JobResponse>(`${this.apiUrl}/api/process`, config);
  }

  /** Run preflight analysis for uploaded LAS files */
  runPreflightForUpload(jobId: string): Observable<PreflightResponse> {
    return this.http.get<PreflightResponse>(`${this.apiUrl}/api/preflight/${jobId}`);
  }

  /** Run preflight analysis for a source folder path */
  runPreflightForPath(sourceFolder: string): Observable<PreflightResponse> {
    return this.http.post<PreflightResponse>(`${this.apiUrl}/api/preflight-path`, {
      source_folder: sourceFolder
    });
  }

  /** Get job status */
  getStatus(jobId: string): Observable<JobStatus> {
    return this.http.get<JobStatus>(`${this.apiUrl}/api/status/${jobId}`);
  }

  /** Poll for status updates every 2 seconds until done */
  pollStatus(jobId: string): Observable<JobStatus> {
    return interval(2000).pipe(
      switchMap(() => this.getStatus(jobId)),
      takeWhile(
        status => LogmergeApiService.ACTIVE_STATUSES.includes(status.status),
        true
      )
    );
  }

  /**
   * Ask the API for a short-lived SAS URL pointing straight at the results
   * archive in Blob Storage.
   *
   * Preferred over `downloadResults` for cloud jobs: streaming a multi-GB ZIP
   * back through the API would blow past App Service's 230s idle timeout.
   *
   * Returns 202 (empty body, `Retry-After` header) while the archive is still
   * being packaged, so callers must check the status code before reading it.
   */
  getResultsDownloadUrl(jobId: string): Observable<HttpResponse<DownloadUrlResponse>> {
    return this.http.get<DownloadUrlResponse>(
      `${this.apiUrl}/api/jobs/${jobId}/download-url`,
      { observe: 'response' }
    );
  }

  /**
   * Download results as ZIP.
   *
   * The backend returns:
   *   * 200 with a Blob body when the archive is on disk (common case).
   *   * 202 with an empty Blob body when packaging is still in progress;
   *     callers should retry after the `Retry-After` header (seconds).
   */
  downloadResults(jobId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/api/results/${jobId}`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  /** Cancel active processing job */
  cancelProcessing(jobId: string): Observable<JobResponse> {
    return this.http.post<JobResponse>(`${this.apiUrl}/api/cancel/${jobId}`, {});
  }

  /**
   * Get processing logs. ``compact`` (default true) asks the server to
   * fold the per-file INFO chatter into per-stage summaries so the UI
   * stays usable on runs with hundreds of LAS files. Pass ``false`` to
   * request the raw, verbose stream for debugging.
   */
  getLogs(jobId?: string, compact: boolean = true): Observable<LogsResponse> {
    const url = jobId ? `${this.apiUrl}/api/logs/${jobId}` : `${this.apiUrl}/api/logs`;
    // ``compact`` is a boolean query param on the server.
    const params = { compact: compact ? 'true' : 'false' };
    return this.http.get<LogsResponse>(url, { params });
  }

  /**
   * Clear the backend's in-memory runtime log buffer. Called on app
   * bootstrap so a fresh browser session starts with a clean slate
   * instead of inheriting logs from a previous run in the same worker.
   */
  resetLogs(): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.apiUrl}/api/logs/reset`, {});
  }

  /** Health check */
  healthCheck(): Observable<any> {
    return this.http.get(`${this.apiUrl}/health`);
  }
}
